import { useMemo, useState, type ReactNode } from 'react'
import { Building2, Check, ChevronDown, ClipboardCopy, Copy, Download, FileJson, FileSpreadsheet, Search, Sheet } from 'lucide-react'
import { bvKort, kiesBvs, meld, meldFout, useGekozenBvs, useStaat } from '../lib/staat'
import { Link } from '../lib/router'
import type { Formaat } from '../lib/export'
import { bedrag } from '../lib/velden'
import { Knop, MenuRegel, Uitklap } from './ui'

/* ===========================================================================
 *  Kleine stukjes die op meer schermen staan
 * =========================================================================== */

export function Tegel({ label, getal, sub, icoon, naar, toon }: {
  label: string; getal: ReactNode; sub?: ReactNode; icoon?: ReactNode; naar?: string; toon?: 'ok' | 'gevaar' | 'warn'
}) {
  const inhoud = (
    <>
      <div className="tegel-label">{label}</div>
      <div className={`tegel-getal ${toon ?? ''}`}>{getal}</div>
      {sub && <div className="tegel-sub">{sub}</div>}
      {icoon && <div className="tegel-glyph">{icoon}</div>}
    </>
  )
  return naar ? <Link naar={naar} className="tegel">{inhoud}</Link> : <div className="tegel">{inhoud}</div>
}

export function Blok({ titel, icoon, rechts, children }: { titel: string; icoon?: ReactNode; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section className="blok">
      <div className="blok-kop">
        <h3>{icoon}{titel}</h3>
        {rechts && <div className="blok-rechts">{rechts}</div>}
      </div>
      {children}
    </section>
  )
}

export function Eigenschap({ label, children, titel }: { label: string; children: ReactNode; titel?: string }) {
  return (
    <div className="eigenschap" title={titel}>
      <span className="eigenschap-label">{label}</span>
      <div className="eigenschap-waarde">{children}</div>
    </div>
  )
}

/** Een bedrag in een cel: rechts, cijfers onder elkaar, negatief in een eigen kleur. */
export function Bedrag({ n, nul = '' }: { n: number | null | undefined; nul?: string }) {
  if (n === null || n === undefined || !Number.isFinite(n)) return <span className="bedrag leeg" />
  if (Math.abs(n) < 0.005) return <span className="bedrag nul">{nul || bedrag(0)}</span>
  return <span className={`bedrag ${n < 0 ? 'neg' : ''}`}>{bedrag(n)}</span>
}

export function Kopieer({ tekst, label }: { tekst: string; label?: string }) {
  return (
    <button type="button" className="mini" title={label ?? 'Kopiëren'} aria-label={label ?? 'Kopiëren'}
      onClick={(e) => {
        e.stopPropagation()
        void navigator.clipboard.writeText(tekst).then(() => meld('Gekopieerd.', 'info'), meldFout)
      }}>
      <Copy size={13} />
    </button>
  )
}

export function Paginakop({ titel, sub, children }: { titel: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="pagina-kop">
      <div className="pagina-titel">
        <h1>{titel}</h1>
        {sub && <p className="stil">{sub}</p>}
      </div>
      {children && <div className="werkbalk">{children}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Welke bv's
 * ------------------------------------------------------------------ */

export function KiesBvs({ waarde, onKies, klein }: {
  /** Zonder waarde: de keuze van de hele app. */
  waarde?: string[]
  onKies?: (codes: string[]) => void
  klein?: boolean
}) {
  const divisies = useStaat((s) => s.divisies)
  const globaal = useGekozenBvs()
  const gekozen = waarde ?? globaal
  const zet = onKies ?? kiesBvs
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')

  const zichtbaar = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return f ? divisies.filter((d) => d.naam.toLowerCase().includes(f) || d.code.includes(f)) : divisies
  }, [divisies, filter])

  const alles = gekozen.length === divisies.length
  const tekst = alles ? `Alle bv's (${divisies.length})` : gekozen.length === 1 ? bvKort(gekozen[0]) : `${gekozen.length} bv's`

  function wissel(code: string) {
    const nu = new Set(gekozen)
    if (nu.has(code)) nu.delete(code)
    else nu.add(code)
    if (nu.size === 0) return
    zet([...nu])
  }

  return (
    <Uitklap open={open} onOpen={setOpen} breed={320} knop={
      <button type="button" className={`kies ${klein ? 'klein' : ''}`} title="Welke administraties">
        <Building2 size={14} />
        <span className="afkap">{tekst}</span>
        <ChevronDown size={14} className="kies-pijl" />
      </button>
    }>
      <div className="menu-zoek">
        <Search size={14} />
        <input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Zoek een bv…" />
      </div>
      <div className="bv-snel">
        <button type="button" className="link-knop" onClick={() => zet(divisies.map((d) => d.code))}>Alles</button>
        {zichtbaar.length !== divisies.length && zichtbaar.length > 0 && (
          <button type="button" className="link-knop" onClick={() => zet(zichtbaar.map((d) => d.code))}>Alleen deze {zichtbaar.length}</button>
        )}
      </div>
      <div className="menu-lijst">
        {zichtbaar.map((d) => (
          <div key={d.code} className="bv-regel">
            <button type="button" className={`menu-regel ${gekozen.includes(d.code) ? 'actief' : ''}`} onClick={() => wissel(d.code)}>
              <span className={`vak ${gekozen.includes(d.code) ? 'aan' : ''}`}>{gekozen.includes(d.code) && <Check size={11} />}</span>
              <span className="menu-tekst afkap" title={`${d.naam} (${d.code})`}>{d.naam}</span>
              <span className="stil mono">{d.code}</span>
            </button>
            <button type="button" className="mini" title="Alleen deze" onClick={() => zet([d.code])}>alleen</button>
          </div>
        ))}
        {!zichtbaar.length && <div className="menu-leeg">Geen bv gevonden.</div>}
      </div>
    </Uitklap>
  )
}

/* ------------------------------------------------------------------ *
 *  Exporteren
 * ------------------------------------------------------------------ */

export function ExportKnop({ onExport, onKopieer, bezig, label = 'Exporteren', klein }: {
  onExport: (f: Formaat) => void | Promise<void>
  onKopieer?: () => void | Promise<void>
  bezig?: boolean
  label?: string
  klein?: boolean
}) {
  const [open, setOpen] = useState(false)
  const doe = (f: () => void | Promise<void>) => { setOpen(false); void Promise.resolve(f()).catch(meldFout) }
  return (
    <Uitklap open={open} onOpen={setOpen} uitlijnen="rechts" breed={230} knop={
      <Knop klein={klein} icoon={<Download size={15} />} bezig={bezig}>{label}</Knop>
    }>
      <MenuRegel icoon={<FileSpreadsheet size={15} />} onClick={() => doe(() => onExport('xlsx'))}>Excel (.xlsx)</MenuRegel>
      <MenuRegel icoon={<Sheet size={15} />} onClick={() => doe(() => onExport('csv'))}>CSV (Excel, Nederlands)</MenuRegel>
      <MenuRegel icoon={<FileJson size={15} />} onClick={() => doe(() => onExport('json'))}>JSON (alles, ruw)</MenuRegel>
      {onKopieer && <MenuRegel icoon={<ClipboardCopy size={15} />} onClick={() => doe(onKopieer)}>Kopiëren als tabel</MenuRegel>}
    </Uitklap>
  )
}
